import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { FaTimes, FaFilter } from 'react-icons/fa';
import { 
  Activity, 
  ActivityPeriod, 
  ACTIVITY_PERIOD_LABELS,
  ACTIVITY_EMOJIS,
  ACTIVITY_LABELS
} from '../../types/activity';
import { activitiesService } from '../../services/supabase/activities.service';
import { useAuth } from '../../context/AuthContext';
import { useUserProfile } from '../../hooks/useUserProfile';
import logger from '../../utils/logger';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';

const DrawerOverlay = styled.div<{ $isOpen: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: 9998;
  display: ${({ $isOpen }) => ($isOpen ? 'block' : 'none')};
  transition: opacity 0.3s ease;
`;

const DrawerContainer = styled.div<{ $isOpen: boolean }>`
  position: fixed;
  top: 0;
  right: 0;
  width: 500px;
  height: 100vh;
  background-color: ${({ theme }) => theme.colors.background};
  box-shadow: -2px 0 10px rgba(0, 0, 0, 0.1);
  z-index: 9999;
  transform: ${({ $isOpen }) => ($isOpen ? 'translateX(0)' : 'translateX(100%)')};
  transition: transform 0.3s ease;
  display: flex;
  flex-direction: column;

  @media (max-width: 768px) {
    width: 100%;
  }
`;

const DrawerHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  background-color: ${({ theme }) => theme.colors.primary};
  color: white;
`;

const DrawerTitle = styled.h2`
  margin: 0;
  font-size: 20px;
  font-weight: 600;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  color: white;
  cursor: pointer;
  padding: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  transition: background-color 0.2s;

  &:hover {
    background-color: rgba(255, 255, 255, 0.1);
  }
`;

const FilterContainer = styled.div`
  padding: 16px 20px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  align-items: center;
`;

const FilterLabel = styled.span`
  font-size: 14px;
  color: ${({ theme }) => theme.colors.textLight};
  display: flex;
  align-items: center;
  gap: 6px;
`;

const FilterChip = styled.button<{ $active: boolean }>`
  padding: 6px 12px;
  border-radius: 16px;
  border: 1px solid ${({ theme }) => theme.colors.border};
  background-color: ${({ $active, theme }) => 
    $active ? theme.colors.primary : 'transparent'};
  color: ${({ $active }) => ($active ? 'white' : 'inherit')};
  font-size: 13px;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background-color: ${({ $active, theme }) => 
      $active ? theme.colors.primaryDark : theme.colors.border};
  }
`;

const ActivitiesContent = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 20px;
`;

const ActivityItem = styled.div`
  padding: 16px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  display: flex;
  gap: 12px;
  transition: background-color 0.2s;

  &:hover {
    background-color: #f5f5f5;
  }

  &:last-child {
    border-bottom: none;
  }
`;

const ActivityIcon = styled.div`
  font-size: 20px;
  flex-shrink: 0;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: ${({ theme }) => theme.colors.background};
  border-radius: 50%;
`;

const ActivityDetails = styled.div`
  flex: 1;
  min-width: 0;
`;

const ActivityHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 4px;
  flex-wrap: wrap;
`;

const ActivityTime = styled.span`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.textLight};
`;

const ActivityName = styled.span`
  font-size: 14px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text};
`;

const ActivityUser = styled.span`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.textLight};
`;

const ActivityNotes = styled.div`
  font-size: 13px;
  color: ${({ theme }) => theme.colors.text};
  margin-top: 4px;
  word-break: break-word;
`;

const ActivityStatus = styled.span<{ $success: boolean }>`
  font-size: 18px;
  flex-shrink: 0;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 40px 20px;
  color: ${({ theme }) => theme.colors.textLight};
`;

const LoadingState = styled.div`
  text-align: center;
  padding: 40px 20px;
  color: ${({ theme }) => theme.colors.textLight};
`;

const LoadMoreButton = styled.button`
  width: 100%;
  padding: 12px;
  background-color: ${({ theme }) => theme.colors.primary};
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
  margin-top: 16px;
  transition: background-color 0.2s;

  &:hover {
    background-color: ${({ theme }) => theme.colors.primaryDark};
  }

  &:disabled {
    background-color: ${({ theme }) => theme.colors.border};
    cursor: not-allowed;
  }
`;

interface ActivityDrawerProps {
  isOpen: boolean;
  onClose: () => void;
}

export const ActivityDrawer: React.FC<ActivityDrawerProps> = ({ isOpen, onClose }) => {
  const { supabaseUser } = useAuth();
  const { userProfile } = useUserProfile();
  const [activities, setActivities] = useState<Activity[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedPeriod, setSelectedPeriod] = useState<ActivityPeriod>('1week');
  const [hasMore, setHasMore] = useState(false);
  const [offset, setOffset] = useState(0);

  const loadActivities = useCallback(async (reset: boolean = false) => {
    if (!userProfile?.agency_id) {
      logger.warn('ActivityDrawer: agency_id manquant', { userProfile });
      return;
    }

    try {
      setLoading(true);
      const currentOffset = reset ? 0 : offset;

      logger.debug('ActivityDrawer: Chargement activités', {
        agencyId: userProfile.agency_id,
        userId: userProfile.role === 'admin' ? undefined : supabaseUser?.id,
        period: selectedPeriod,
        offset: currentOffset
      });

      const response = await activitiesService.getActivities({
        agencyId: userProfile.agency_id,
        userId: userProfile.role === 'admin' ? undefined : supabaseUser?.id,
        period: selectedPeriod,
        limit: 50,
        offset: currentOffset
      });

      logger.debug('ActivityDrawer: Activités reçues', {
        count: response.activities.length,
        hasMore: response.hasMore
      });

      if (reset) {
        setActivities(response.activities);
        setOffset(50);
      } else {
        setActivities(prev => [...prev, ...response.activities]);
        setOffset(prev => prev + 50);
      }

      setHasMore(response.hasMore);
    } catch (error) {
      logger.error('Erreur lors du chargement des activités:', error);
    } finally {
      setLoading(false);
    }
  }, [userProfile?.agency_id, userProfile?.role, supabaseUser?.id, selectedPeriod, offset]);

  useEffect(() => {
    if (isOpen) {
      loadActivities(true);
    }
  }, [isOpen, selectedPeriod]);

  const handlePeriodChange = (period: ActivityPeriod) => {
    setSelectedPeriod(period);
    setOffset(0);
  };

  const formatActivityTime = (date: Date) => {
    const now = new Date();
    const diffInHours = (now.getTime() - date.getTime()) / (1000 * 60 * 60);

    if (diffInHours < 1) {
      const diffInMinutes = Math.floor(diffInHours * 60);
      return `Il y a ${diffInMinutes} min`;
    } else if (diffInHours < 24) {
      return `Il y a ${Math.floor(diffInHours)}h`;
    } else {
      return format(date, 'dd MMM à HH:mm', { locale: fr });
    }
  };

  return (
    <>
      <DrawerOverlay $isOpen={isOpen} onClick={onClose} />
      <DrawerContainer $isOpen={isOpen}>
        <DrawerHeader>
          <DrawerTitle>Activités</DrawerTitle>
          <CloseButton onClick={onClose}>
            <FaTimes size={20} />
          </CloseButton>
        </DrawerHeader>

        <FilterContainer>
          <FilterLabel>
            <FaFilter size={12} />
            Période :
          </FilterLabel>
          {(Object.keys(ACTIVITY_PERIOD_LABELS) as ActivityPeriod[]).map((period) => (
            <FilterChip
              key={period}
              $active={selectedPeriod === period}
              onClick={() => handlePeriodChange(period)}
            >
              {ACTIVITY_PERIOD_LABELS[period]}
            </FilterChip>
          ))}
        </FilterContainer>

        <ActivitiesContent>
          {loading && activities.length === 0 ? (
            <LoadingState>Chargement des activités...</LoadingState>
          ) : activities.length === 0 ? (
            <EmptyState>Aucune activité pour cette période</EmptyState>
          ) : (
            <>
              {activities.map((activity) => (
                <ActivityItem key={activity.id}>
                  <ActivityIcon>
                    {ACTIVITY_EMOJIS[activity.activityName]}
                  </ActivityIcon>
                  <ActivityDetails>
                    <ActivityHeader>
                      <ActivityTime>{formatActivityTime(activity.occurredAt)}</ActivityTime>
                      <ActivityName>{ACTIVITY_LABELS[activity.activityName]}</ActivityName>
                      {activity.userName && (
                        <ActivityUser>par {activity.userName}</ActivityUser>
                      )}
                    </ActivityHeader>
                    {activity.notes && (
                      <ActivityNotes>{activity.notes}</ActivityNotes>
                    )}
                  </ActivityDetails>
                  <ActivityStatus $success={activity.result === 'success'}>
                    {activity.result === 'success' ? '✅' : '❌'}
                  </ActivityStatus>
                </ActivityItem>
              ))}
              {hasMore && (
                <LoadMoreButton
                  onClick={() => loadActivities(false)}
                  disabled={loading}
                >
                  {loading ? 'Chargement...' : 'Charger plus'}
                </LoadMoreButton>
              )}
            </>
          )}
        </ActivitiesContent>
      </DrawerContainer>
    </>
  );
};
